import { type Driver, GraphQLDriversRequester } from '@cobliteam/api-lib';
import type { BatchCreateDriverItemInput } from '@cobliteam/api-lib/src/__generated__/graphql';
import type { DriverNames, UUID } from '@cobliteam/common-types';
import { useTranslation } from '@cobliteam/i18n';
import Alert from '@cobliteam/ui/components/Alert';
import { type UseQueryOptions, type UseSuspenseQueryOptions, useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { trimDriverName } from './utils';

export const QUERY_KEY = 'DRIVERS';
export const QUERY_KEY_WITH_VEHICLES = 'DRIVERS_WITH_VEHICLES';
export const QUERY_KEY_NAMES = 'DRIVER_NAMES';

type GetDriversParams = { options?: Partial<UseQueryOptions<Driver[]>> } | undefined;
type GetDriverNamesParams = { options?: Partial<UseQueryOptions<DriverNames>> } | undefined;

const useDriver = () => {
  const graphqlDriverRequester = new GraphQLDriversRequester();

  const queryClient = useQueryClient();
  const { t } = useTranslation('drivers');

  const getDrivers = (params?: GetDriversParams) => {
    return useQuery<Driver[]>({
      ...params?.options,
      queryKey: [QUERY_KEY],
      queryFn: () => graphqlDriverRequester.getDrivers(),
      retry: params?.options?.retry ?? 0,
      enabled: params?.options?.enabled ?? true,
      refetchOnMount: params?.options?.refetchOnMount ?? true,
      refetchOnWindowFocus: params?.options?.refetchOnWindowFocus ?? true,
      staleTime: params?.options?.staleTime ?? Number.POSITIVE_INFINITY,
    });
  };

  const getDriversSuspense = (options?: Partial<UseSuspenseQueryOptions<Driver[]>>) => {
    return useSuspenseQuery<Driver[]>({
      ...options,
      queryKey: [QUERY_KEY],
      queryFn: () => graphqlDriverRequester.getDrivers(),
      retry: options?.retry ?? 0,
      refetchOnMount: options?.refetchOnMount ?? true,
      refetchOnWindowFocus: options?.refetchOnWindowFocus ?? true,
      staleTime: options?.staleTime ?? Number.POSITIVE_INFINITY,
    });
  };

  const getDriversWithVehicles = (params?: GetDriversParams) => {
    return useQuery<Driver[]>({
      ...params?.options,
      queryKey: [QUERY_KEY_WITH_VEHICLES],
      queryFn: () => graphqlDriverRequester.getDriversWithVehicles(),
      retry: params?.options?.retry ?? 0,
      enabled: params?.options?.enabled ?? true,
      refetchOnMount: params?.options?.refetchOnMount ?? true,
      refetchOnWindowFocus: params?.options?.refetchOnWindowFocus ?? true,
      staleTime: params?.options?.staleTime ?? Number.POSITIVE_INFINITY,
    });
  };

  const getDriverNames = (params?: GetDriverNamesParams) => {
    return useQuery<DriverNames>({
      ...params?.options,
      queryKey: [QUERY_KEY_NAMES],
      queryFn: () => graphqlDriverRequester.getDriverNames(),
      retry: params?.options?.retry ?? 0,
      enabled: params?.options?.enabled ?? true,
      refetchOnMount: params?.options?.refetchOnMount ?? true,
      refetchOnWindowFocus: params?.options?.refetchOnWindowFocus ?? true,
      staleTime: params?.options?.staleTime ?? Number.POSITIVE_INFINITY,
    });
  };

  const prefetchDrivers = () => {
    queryClient.prefetchQuery({
      queryKey: [QUERY_KEY],
      queryFn: () => graphqlDriverRequester.getDrivers(),
    });
  };

  const prefetchDriversWithVehicles = () => {
    queryClient.prefetchQuery({
      queryKey: [QUERY_KEY_WITH_VEHICLES],
      queryFn: () => graphqlDriverRequester.getDriversWithVehicles(),
    });
  };

  const useSaveDriver = () => {
    return useMutation<Driver, unknown, Driver>({
      mutationKey: [QUERY_KEY],
      mutationFn: async (driver) => graphqlDriverRequester.saveDriver(trimDriverName(driver)),
      onMutate: async (driver) => {
        await queryClient.cancelQueries({ queryKey: [QUERY_KEY] });
        const previousDrivers = queryClient.getQueryData<Driver[]>([QUERY_KEY]);
        queryClient.setQueryData<Driver[]>([QUERY_KEY], (oldDrivers) => {
          if (oldDrivers) return oldDrivers.map((d) => (d.id === driver.id ? driver : d));
        });
        return { previousDrivers };
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
        Alert.success(t('hooks.save_driver_success'));
      },
      onError: (err: any, variables, context: any) => {
        queryClient.setQueryData([QUERY_KEY], context.previousDrivers);
        Alert.error(t('hooks.save_driver_error'));
      },
    });
  };

  const useCreateDriversBatch = () => {
    return useMutation<Driver[], unknown, BatchCreateDriverItemInput[]>({
      mutationFn: async (drivers) => graphqlDriverRequester.createDriversBatch(drivers),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
        Alert.success(t('sheet_import.import_success'));
      },
    });
  };

  const useDeleteDriver = () => {
    return useMutation<UUID, unknown, UUID>({
      mutationFn: async (id) => graphqlDriverRequester.deleteDriver(id),
      onSuccess: () => {
        Alert.success(t('hooks.delete_driver_success'));
        queryClient.resetQueries({ queryKey: [QUERY_KEY] });
        queryClient.resetQueries({ queryKey: ['QUERY_GET_IDENTIFICATIONS_TOKENS_V2_KEY'] });
        queryClient.resetQueries({ queryKey: ['IDENTIFICATION_TOKEN_V2'] });
      },
      onError: () => {
        Alert.error(t('hooks.delete_driver_error'));
      },
    });
  };

  return {
    getDrivers,
    getDriversSuspense,
    getDriverNames,
    getDriversWithVehicles,
    useSaveDriver,
    useCreateDriversBatch,
    useDeleteDriver,
    prefetchDrivers,
    prefetchDriversWithVehicles,
  };
};

export default useDriver;
